import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Badge, Button, useToast } from '@api-hub/ui';
import { api, describeError } from '../lib/api';
import { t } from '../lib/i18n';
import type { SessionUser } from '../App';

/** Sidebar grouping mirrors the operational model: observe, configure, grant. */
const NAV_GROUPS: Array<{
  title: string;
  items: Array<{ to: string; label: string; minimumRole?: string }>;
}> = [
  {
    title: t('nav.overview'),
    items: [
      { to: '/', label: t('nav.dashboard') },
      { to: '/requests', label: t('nav.requests') },
      { to: '/usage', label: t('nav.usage') },
      { to: '/audit', label: t('nav.audit') },
    ],
  },
  {
    title: t('nav.providers'),
    items: [
      { to: '/providers', label: t('nav.providerList') },
      { to: '/connections', label: t('nav.connections') },
      { to: '/upstream-mcp', label: t('nav.upstreamMcp') },
    ],
  },
  {
    title: t('nav.catalog'),
    items: [
      { to: '/services', label: t('nav.services') },
      { to: '/operations', label: t('nav.operations') },
      { to: '/model-aliases', label: t('nav.modelAliases') },
      { to: '/openapi-import', label: t('nav.openapiImport') },
      { to: '/knowledge', label: t('nav.knowledge') },
      { to: '/catalog-versions', label: t('nav.catalogVersions') },
    ],
  },
  {
    title: t('nav.clients'),
    items: [
      { to: '/client-apps', label: t('nav.clientApps') },
      { to: '/hub-keys', label: t('nav.hubKeys') },
      { to: '/developer-tokens', label: t('nav.developerTokens') },
    ],
  },
  {
    title: t('nav.agents'),
    items: [
      { to: '/toolsets', label: t('nav.toolsets') },
      { to: '/mcp-preview', label: t('nav.mcpPreview') },
      { to: '/integration-packs', label: t('nav.packs') },
    ],
  },
  {
    title: t('nav.operationsSection'),
    items: [
      { to: '/test-console', label: t('nav.testConsole') },
      { to: '/users', label: t('nav.users') },
      { to: '/api-reference', label: t('nav.apiReference') },
      { to: '/settings', label: t('nav.settings') },
    ],
  },
];

export function Layout({ user, onSignedOut }: { user: SessionUser; onSignedOut: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();

  const signOut = async () => {
    try {
      await api.post('/api/admin/auth/logout');
      onSignedOut();
      navigate('/');
    } catch (error) {
      const described = describeError(error);
      toast.error(described.title, described.body);
    }
  };

  return (
    <div className="ah-shell">
      <nav className="ah-sidebar" aria-label="Nawigacja główna">
        <div className="ah-brand">
          <div>
            <strong>{t('app.title')}</strong>
            <span>{t('app.subtitle')}</span>
          </div>
        </div>
        {NAV_GROUPS.map((group) => (
          <div key={group.title} className="ah-nav-group">
            <h3>{group.title}</h3>
            {group.items.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.to === '/'} className="ah-nav-link">
                {item.label}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className="ah-main">
        <header className="ah-topbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Badge tone={user.role === 'OWNER' ? 'info' : 'neutral'}>{user.role}</Badge>
            <span style={{ fontSize: 13, color: 'var(--ah-text-muted)' }}>{user.email}</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button size="sm" onClick={() => navigate('/change-password')}>
              {t('auth.changePassword')}
            </Button>
            <Button size="sm" onClick={signOut}>
              {t('auth.signOut')}
            </Button>
          </div>
        </header>
        <main className="ah-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function PageHeader(props: {
  title: string;
  description?: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="ah-page-header">
      <div>
        <h1>{props.title}</h1>
        {props.description && <p>{props.description}</p>}
      </div>
      {props.actions && <div style={{ display: 'flex', gap: 8 }}>{props.actions}</div>}
    </div>
  );
}
